import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { permissionsOf } from '@syndiqia/platform/permissions';
import { demoPersonaOf, storePersona, type DemoPersona } from './persona';
export type { DemoPersona };
import { useApi } from './api';

import type { Permission, Role, StaffRole } from '@syndiqia/platform/permissions';
export type { Permission, Role, StaffRole };

export interface Membership {
  tenantId: string;
  slug: string;
  name: string;
  /** Syndicate of divided co-ownership, or owners' association (non-profit with servitudes): the rules differ. */
  legalStructure: 'divided_coownership' | 'npo_with_servitudes';
  kind: 'client' | 'demo';
  /** A membership; a demo opened by Syndiqia staff; or a support access the client approved (read-only). */
  access: 'member' | 'demo' | 'support' | 'onboarding';
  /** End of a support access. */
  expiresAt: string | null;
  roles: Role[];
  permissions: Permission[];
  /** The syndicate's email address for requests (demandes@<slug>.syndiqia.com), when email intake is on. */
  inboundAddress: string | null;
  invoiceAddress: string | null;
}

export interface Me {
  user: { id: string; email: string | null; displayName: string | null; locale: 'fr' | 'en' };
  memberships: Membership[];
  /** Syndiqia staff roles (docs/engineering/roles.md); empty for everyone else. */
  staff: StaffRole[];
}

export function useMe() {
  const api = useApi();
  return useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/me') });
}

interface Syndicate extends Membership {
  /** In a demo: the fictional account staff currently act as, and how to change it. */
  persona: DemoPersona | null;
  setPersona(persona: DemoPersona | null): void;
  /** What the caller may do here; the server enforces the same permissions. */
  can(permission: Permission): boolean;
  /** Prefix for this syndicate's API routes. */
  api: string;
  /** Prefix for this syndicate's pages. */
  base: string;
}

const SyndicateContext = createContext<Syndicate | null>(null);

export function SyndicateProvider({ membership, children }: { membership: Membership; children: ReactNode }) {
  const [persona, setPersonaState] = useState<DemoPersona | null>(() => (membership.access === 'demo' ? demoPersonaOf(membership.tenantId) : null));
  // In a demo, "voir comme…" shows what that role sees; the server applies the same persona (x-demo-persona).
  const roles = persona ? [persona] : membership.roles;
  const permissions = persona ? permissionsOf(roles) : membership.permissions;
  const value: Syndicate = {
    ...membership,
    roles,
    permissions,
    persona,
    setPersona: (p) => {
      storePersona(membership.tenantId, p);
      setPersonaState(p);
    },
    can: (permission) => permissions.includes(permission),
    api: `/api/t/${membership.tenantId}`,
    base: `/s/${membership.slug}`,
  };
  return <SyndicateContext.Provider value={value}>{children}</SyndicateContext.Provider>;
}

/** The syndicate currently open in the app, with the caller's roles in it. */
export function useSyndicate(): Syndicate {
  const ctx = useContext(SyndicateContext);
  if (!ctx) throw new Error('useSyndicate outside SyndicateProvider');
  return ctx;
}
